import { TestCategory, TestStatus } from "types/testTypes";

export const TEST_CATEGORIES_WITHOUT_EYE_TRACKING: ReadonlySet<TestCategory> = new Set([
  TestCategory.VisualAcuity,
  TestCategory.ColorVision,
  TestCategory.AmslerGrid,
  TestCategory.Dyscalculia,
  TestCategory.StereoAcuity,
  TestCategory.WisconsinCardSorting,
  TestCategory.ContrastSensitivity,
  TestCategory.Kinetic,
]);

const PROMPTABLE_TEST_STATUSES: ReadonlySet<TestStatus> = new Set([
  TestStatus.StartRequested,
  TestStatus.Calibration,
  TestStatus.VirtualAssistant,
  TestStatus.Started,
  TestStatus.Paused,
  TestStatus.WaitingForNextEye,
]);

export type EyeTrackingHealthSeverity = "warning" | "error";

export interface EyeTrackingUnhealthyReport {
  startUuid: string | null;
  recoveryAttempts: number | null;
  scene: string | null;
  reason: string | null;
  severity: EyeTrackingHealthSeverity;
}

function parseStartUuid(data: Record<string, unknown>): string | null {
  return typeof data.startUuid === "string" && data.startUuid ? data.startUuid : null;
}

export function parseEyeTrackingUnhealthyMessage(msg: unknown): EyeTrackingUnhealthyReport | null {
  const data = (msg ?? {}) as Record<string, unknown>;
  if (data.healthy !== false && data.healthy !== "false") return null;

  return {
    startUuid: parseStartUuid(data),
    recoveryAttempts:
      typeof data.recoveryAttempts === "number" && Number.isFinite(data.recoveryAttempts)
        ? data.recoveryAttempts
        : null,
    scene: typeof data.scene === "string" ? data.scene : null,
    reason: typeof data.reason === "string" ? data.reason : null,
    severity: data.severity === "warning" ? "warning" : "error",
  };
}

export function parseEyeTrackingRecoveredRun(msg: unknown): string | null {
  const data = (msg ?? {}) as Record<string, unknown>;
  if (data.healthy !== true && data.healthy !== "true") return null;

  return parseStartUuid(data);
}

export function shouldPromptDeviceRestart(params: {
  report: EyeTrackingUnhealthyReport;
  testCategory: TestCategory | string | null | undefined;
  testStatus: TestStatus | null | undefined;
  activeTestId: string | null | undefined;
}): boolean {
  const { report, testCategory, testStatus, activeTestId } = params;

  if (!testCategory || TEST_CATEGORIES_WITHOUT_EYE_TRACKING.has(testCategory as TestCategory)) {
    return false;
  }

  if (!testStatus || !PROMPTABLE_TEST_STATUSES.has(testStatus)) {
    return false;
  }

  if (!report.startUuid || !activeTestId || report.startUuid !== activeTestId) {
    return false;
  }

  return true;
}

const EYE_TRACKING_VALUE_KEY = /gaze|pupil|openness|percentage/i;

const POSITIVE_ONLY_KEY = /pupil|openness/i;

const NOT_A_READING_KEY =
  /time|stamp|index|count|valid|delta|distance|error|velocity|speed|acceleration|duration/i;

const MAX_SEARCH_DEPTH = 4;

function parseIfJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function readingHasLiveValue(value: unknown, positiveOnly: boolean, depth: number): boolean {
  const parsed = parseIfJson(value);
  if (typeof parsed === "number") {
    if (!Number.isFinite(parsed)) return false;
    return positiveOnly ? parsed > 0 : parsed !== 0;
  }
  if (!parsed || typeof parsed !== "object" || depth > MAX_SEARCH_DEPTH) return false;

  return Object.entries(parsed as Record<string, unknown>).some(
    ([key, nested]) =>
      !NOT_A_READING_KEY.test(key) &&
      readingHasLiveValue(nested, positiveOnly || POSITIVE_ONLY_KEY.test(key), depth + 1),
  );
}

export function hasEyeTrackingSignal(message: unknown, depth = 0): boolean {
  const parsed = parseIfJson(message);
  if (!parsed || typeof parsed !== "object" || depth > MAX_SEARCH_DEPTH) return false;

  return Object.entries(parsed as Record<string, unknown>).some(([key, value]) => {
    if (NOT_A_READING_KEY.test(key)) return false;
    if (EYE_TRACKING_VALUE_KEY.test(key)) {
      return readingHasLiveValue(value, POSITIVE_ONLY_KEY.test(key), depth);
    }
    return hasEyeTrackingSignal(value, depth + 1);
  });
}
